import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { es } from '@/i18n/es';
import { routes } from '@/lib/routes';
import { useSessionStatus } from '@/store/sessionStore';
import { useUiStore } from '@/store/uiStore';
import { Header } from './Header';
import { BottomNav, SideNav } from './Navigation';
import { SiteFooter } from './SiteFooter';

/**
 * Caparazón de la app: header con toggle y buscador, navegación inferior en
 * mobile y lateral en desktop. El lector usa su propio layout, sin esta capa.
 */
export function AppShell({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const status = useSessionStatus();
  const setOverrideType = useUiStore((s) => s.setOverrideType);

  // Salir de una historia devuelve el acento a la preferencia guardada.
  useEffect(() => {
    return () => setOverrideType(null);
  }, [location.pathname, setOverrideType]);

  // Cada navegación arranca arriba y mueve el foco al contenido.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * Sesión iniciada pero sin perfil: sin handle no se puede publicar,
   * comentar ni seguir a nadie, y el header no tiene qué mostrar. Antes se
   * podía navegar indefinidamente en ese limbo; ahora el onboarding es un
   * paso obligatorio, no una pantalla que se puede saltear.
   */
  if (status === 'needs-onboarding') {
    return <Navigate to={routes.onboarding} state={{ from: location.pathname }} replace />;
  }

  return (
    // `app-ground` trae la textura en modo oscuro. El lector y la portada
    // quedan afuera solos: los dos viven fuera de este caparazón.
    <div className="app-ground min-h-dvh">
      <a href="#contenido" className="skip-link">
        {es.app.skipToContent}
      </a>
      <Header />
      <div className="mx-auto flex max-w-shell gap-6 px-4 pb-[calc(var(--bottom-nav-height)+2rem)] pt-5 md:pb-10">
        <SideNav />
        <main id="contenido" tabIndex={-1} className="min-w-0 flex-1 outline-none">
          {/* Como ruta padre usa Outlet; envolviendo una pantalla suelta, children. */}
          {children ?? <Outlet />}
          <SiteFooter />
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
